import React, { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { Play } from "lucide-react";
import { ProductGalleryItem } from "../../../../../../services/helpers/ProductImageHelper";
import "./styles/GalleryViewer.css";

interface GalleryViewerProps {
  /** Images + videos in payload order (backend order = display order). */
  items: ProductGalleryItem[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
  productTitle?: string;
}

const GalleryViewer: React.FC<GalleryViewerProps> = ({
  items,
  initialIndex,
  isOpen,
  onClose,
  productTitle = "Product",
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [pinchStart, setPinchStart] = useState(0);

  useEffect(() => {
    setCurrentIndex(initialIndex);
    setZoom(1);
    setPos({ x: 0, y: 0 });
  }, [initialIndex, isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setCurrentIndex((i) => Math.min(i + 1, items.length - 1));
      if (e.key === "ArrowLeft") setCurrentIndex((i) => Math.max(i - 1, 0));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, items.length, onClose]);

  const current = items[currentIndex];
  const isVideo = current?.kind === "video";

  // Zoom / pan / pinch apply to IMAGES only — the video has native controls.
  const handleWheel = (e: React.WheelEvent) => {
    if (isVideo) return;
    e.preventDefault();
    const next = Math.max(0.5, Math.min(3, zoom + (e.deltaY > 0 ? -0.2 : 0.2)));
    setZoom(next);
    if (next <= 1) setPos({ x: 0, y: 0 });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isVideo) return;
    if (e.touches.length === 2) {
      setPinchStart(Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY,
      ));
    } else if (zoom > 1) {
      setPanStart({ x: e.touches[0].clientX - pos.x, y: e.touches[0].clientY - pos.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isVideo) return;
    if (e.touches.length === 2 && pinchStart > 0) {
      const dist = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY,
      );
      const next = Math.max(0.5, Math.min(3, zoom * (dist / pinchStart)));
      setZoom(next);
      if (next <= 1) setPos({ x: 0, y: 0 });
    } else if (zoom > 1 && e.touches.length === 1) {
      setPos({ x: e.touches[0].clientX - panStart.x, y: e.touches[0].clientY - panStart.y });
    }
  };

  if (!isOpen || !current) return null;

  return (
    <div className="gv-root">
      <div className="gv-backdrop" onClick={onClose} />

      <div className="gv-header">
        <button className="gv-close" onClick={onClose} aria-label="Close">
          <FiX size={24} />
        </button>
        {items.length > 1 && (
          <span className="gv-counter">{currentIndex + 1} / {items.length}</span>
        )}
      </div>

      <div
        className="gv-stage"
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => setPinchStart(0)}
        onClick={() => {
          if (isVideo) return; // native controls own video clicks
          if (zoom === 1) setZoom(2);
          else { setZoom(1); setPos({ x: 0, y: 0 }); }
        }}
      >
        {isVideo ? (
          /* User explicitly opened this video — play it, with sound + controls. */
          <video
            key={current.url}
            className="gv-video"
            src={current.url}
            controls
            autoPlay
            playsInline
          />
        ) : (
          <img
            src={current.url}
            alt={`${productTitle} - ${currentIndex + 1}`}
            className="gv-img"
            style={{ transform: `scale(${zoom}) translate(${pos.x / zoom}px,${pos.y / zoom}px)` }}
            draggable={false}
          />
        )}
      </div>

      {/* Thumbnails */}
      <div className="gv-thumbs">
        {items.map((item, i) => (
          <button
            key={i}
            className={`gv-thumb${i === currentIndex ? " gv-thumb--active" : ""}`}
            onClick={() => { setCurrentIndex(i); setZoom(1); setPos({ x: 0, y: 0 }); }}
          >
            {item.kind === "video" ? (
              <>
                <video src={item.url} muted playsInline preload="metadata" />
                <span className="gv-thumb-play" aria-hidden>
                  <Play size={10} fill="currentColor" />
                </span>
              </>
            ) : (
              <img src={item.url} alt={`Thumb ${i + 1}`} />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default GalleryViewer;
